import React from 'react';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Layout } from 'src/components/layout-wrapper/Layout';
import Loader from 'src/components/content/loader';
import Alert from 'src/components/alert/Alert';
import SubscriptionPlanPopup from 'src/pages/payment-new/SubscriptionPlanPopup';
import { ReactivateOrderSummary } from './components/ReactivateOrderSummary';
import { ReactivatePaymentDetails } from './components/ReactivatePaymentDetails';
import { ReactivatePaymentForm } from './components/ReactivatePaymentForm';
import { useReactivationFlow } from './hooks/useReactivationFlow';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { clearReactivationState } from 'src/store/reactivation';
import {
  clearCampaignFromSession,
  getCampaignFromSession,
} from 'src/util/campaign.util';
import { getCampaignDisplay } from './utils/campaignDisplay';
import { AuthenticationAction, logOut } from 'src/lib/auth/actions';
import history from 'src/lib/history';
import logo from 'src/assets/images/logo-new.svg';
import styles from './reactivate-subscription.module.scss';
import { useDispatch } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import { DEFAULT_DISCOUNT_ROLLOVER_PERCENT } from './constants';

const ReactivateSubscriptionPage = () => {
  const dispatch = useAppDispatch();
  const baseDispatch: ThunkDispatch<AuthenticationAction, never, AnyAction> =
    useDispatch();

  const { campaignType, subscription: reactivationSubscription } =
    useAppSelector(s => s.reactivation);
  const discountPercent =
    reactivationSubscription?.invoiceDiscount?.percent_off ?? null;
  const { pageTitle, badgeLabel, ctaLabel, showFreeTrial } = getCampaignDisplay(
    campaignType,
    discountPercent || DEFAULT_DISCOUNT_ROLLOVER_PERCENT
  );

  const {
    user,
    subscription,
    selectedPlanId,
    priceId,
    productId,
    totalPrice,
    paymentMethod,
    initialPaymentMethod,
    hasCC,
    isLoading,
    isSubmitting,
    errorMessage,
    showPlanPopup,
    showPlanSelectModal,
    noIntentAvailable,
    setPaymentMethod,
    setErrorMessage,
    setShowPlanPopup,
    setShowPlanSelectModal,
    setWalletPaymentIntentId,
    setNoPaymentIntentAvailable,
    handleReactivate,
    handlePaymentFormSubmit,
  } = useReactivationFlow();

  const handleClose = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to cancel? You will be logged out.'
    );
    if (!confirmed) return;

    dispatch(clearReactivationState());
    clearCampaignFromSession();
    baseDispatch(logOut());
    history.replace('/login-account');
  };

  if (isLoading) {
    return (
      <Layout module="login" hideHeader showLighterColors>
        <Loader />
        <Typography sx={{ textAlign: 'center', marginTop: -10 }}>
          Fetching details...
        </Typography>
      </Layout>
    );
  }

  return (
    <Layout hideHeader showLighterColors>
      <div className={styles.reactivateContainer}>
        <Box className={styles.topHeader}>
          <a href="https://joinequ.com/">
            <img src={logo} alt="equ" className={styles.logo} />
          </a>
          <IconButton
            onClick={handleClose}
            className={styles.closeButton}
            aria-label="Close">
            <CloseIcon sx={{ color: '#101820', fontSize: '20px' }} />
          </IconButton>
        </Box>

        <Stack className={styles.contentWrapper}>
          <Typography className={styles.heading}>{pageTitle}</Typography>

          <Alert message={errorMessage} />

          <ReactivateOrderSummary
            subscription={subscription}
            onChangePlan={() => setShowPlanPopup(true)}
            badgeLabel={badgeLabel}
            showFreeTrial={showFreeTrial}
            campaignType={campaignType}
          />

          {hasCC ? (
            <ReactivatePaymentDetails
              user={user}
              paymentMethod={paymentMethod || initialPaymentMethod || undefined}
              setPaymentMethod={setPaymentMethod}
              onReactivate={handleReactivate}
              isSubmitting={isSubmitting}
              priceId={priceId}
              totalPrice={totalPrice}
              setWalletPaymentIntentId={setWalletPaymentIntentId}
              setNoIntentAvailable={setNoPaymentIntentAvailable}
              setErrorMessage={setErrorMessage}
              ctaLabel={ctaLabel}
              campaignCode={getCampaignFromSession()}
            />
          ) : (
            <ReactivatePaymentForm
              selectedPlanId={selectedPlanId}
              priceId={priceId}
              finalPrice={Number(totalPrice)}
              productId={productId}
              handleSubmit={handlePaymentFormSubmit}
              paymentMethod={paymentMethod}
              setPaymentMethod={setPaymentMethod}
              errorMessage={errorMessage}
              setErrorMessage={setErrorMessage}
              setWalletPaymentIntentId={setWalletPaymentIntentId}
              noIntentAvailable={noIntentAvailable}
              setNoIntentAvailable={setNoPaymentIntentAvailable}
              ctaLabel={ctaLabel}
            />
          )}
        </Stack>

        {(showPlanPopup || showPlanSelectModal) && (
          <SubscriptionPlanPopup
            setPopupVisible={val => {
              setShowPlanPopup(val);
              setShowPlanSelectModal(val);
            }}
            user={user}
            planRequest={null}
            isTwelveMonthParam={false}
            title={
              showPlanSelectModal
                ? 'Reactivate subscription'
                : 'Choose subscription'
            }
            confirmButtonLabel={
              showPlanSelectModal ? 'Next' : 'Change your program'
            }
            hideCloseButton={showPlanSelectModal}
          />
        )}
      </div>
    </Layout>
  );
};

export default ReactivateSubscriptionPage;
